import React, { useEffect, useState, useRef, useCallback, useImperativeHandle, forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toastr } from "react-redux-toastr";
import { Col, Form, Row, ButtonGroup, Alert, OverlayTrigger, Button, Table } from "react-bootstrap";
import RangeSlider from "react-range-slider-input";
import { PiInfo } from "react-icons/pi";
import { FaCheck } from "react-icons/fa6";
import { FiPlus } from "react-icons/fi";
import { HexColorPicker } from "react-colorful";
import { RiDeleteBinLine } from "react-icons/ri";
import { TbMapPinPlus } from "react-icons/tb";
import { PiPolygonDuotone } from "react-icons/pi";
import { GrUndo } from "react-icons/gr";
import area from "@turf/area";

import CustomButton from "../../CustomButton";
import { createZone, editZone } from "../../../action/CompaniesActions";
import { getContrastColor, getPolygonFeature, isSimplePolygon } from "../../helpers/helperZones";

import "react-range-slider-input/dist/style.css";
import { createVertexIcon } from "../../helpers/iconHelpers";

const ZoneForm = forwardRef((props, ref) => {
    const dispatch = useDispatch();
    const { getZones, hasZones, map, mapState, behavior, platform, zoneNames } = props;
    const defaultErrors = {
        zoneName: '',
        address: '',
        latitude: '',
        longitude: '',
        radius: '',
        coordinates : '',
    };
    const { t } = useTranslation('translation', { keyPrefix: 'zonesPage' });
    const user = useSelector(state => state.user.user);
    const company = useSelector(state => state.company.company);
    const zones = useSelector(state => state.zones.zonesList);
    const mode = useSelector(state => state.zones.mode);
    const editedZoneId = useSelector(state => state.zones.editedZoneId);
    const values = useSelector(state => state.zones.zoneData);
    const presetColors = useSelector(state => state.zones.presetColors);
    const companyId = company.company_id;
    const editedZoneGroupRef = useRef(null);
    const drawingPolygonRef = useRef(null);
    const drawingPolylineRef = useRef(null);
    const polygonCoordsRef = useRef([]);
    const animationTokens = useRef(new Map());

    const [errors, setErrors] = useState({...defaultErrors});
    const [suggestions, setSuggestions] = useState([]);
    const [polygonMode, setPolygonMode] = useState('none');

    const k = (v, radius) => Math.round(v / 2 + radius * v / 10);

    useImperativeHandle(ref, () => ({
        cancel: cancelForm
    }));

    useEffect(() => {
        polygonCoordsRef.current = values.polygonCoords;
    }, [values.polygonCoords]);

    const selectorTypeData = [
        {
            type: 'CIRCLE',
            label: t('circle'),
            labelCaption: t('createCircularZone')
        },
        {
            type: 'POLYGON',
            label: t('customShape'),
            labelCaption: t('createShapeZone')
        }
    ]

    useEffect(() => {
        if (!behavior) return;

        if (
            values.type === 'POLYGON' &&
            polygonMode === 'create'
        ) {
            behavior.disable(
                H.mapevents.Behavior.Feature.DBL_TAP_ZOOM
            );
        } else {
            behavior.enable(
                H.mapevents.Behavior.Feature.DBL_TAP_ZOOM
            );
        }

        return () => {
            behavior.enable(
                H.mapevents.Behavior.Feature.DBL_TAP_ZOOM
            );
        };
    }, [behavior, values.type, polygonMode]);

    useEffect(() => {
        if (mode === 'EDIT' && editedZoneId) {
            const zone = zones.find(z => z.id === editedZoneId);
            if (zone) {
                const zoneColor = zone.shape_color || '#F569CA';
                if (!presetColors.includes(zoneColor)) {
                    setPresetColors(zoneColor);
                }
                dispatch({
                    type: 'INITIALIZE_ZONE_DATA',
                    payload: {
                        type: zone.shape_type || 'CIRCLE',
                        zoneName: zone.zone_name || '',
                        address: zone.address || '',
                        latitude: zone.shape_points[0].lat || '',
                        longitude: zone.shape_points[0].lng || '',
                        radius: zone.circle_radius || 0.1,
                        color: zoneColor,
                        polygonCoords: zone.shape_points || []
                    }
                });
            }
        } else {
            dispatch({
                type: 'RESET_ZONE_DATA',
                payload: {
                    type: 'CIRCLE',
                    latitude: mapState.lat,
                    longitude: mapState.lng,
                }
            });
        }
    }, [editedZoneId]);

    useEffect(() => {
        if (!map || !behavior) return;

        if (values.type !== 'CIRCLE') return;

        if (mode === 'EDIT') {
            detachEditedZoneGroup();
        }

        const existing = map.getObjects().filter(obj =>
            obj instanceof H.map.Group && obj.getRemoteId() === 'dragableZoneObject'
        );
        map.removeObjects(existing);

        const dragableZoneObject = new H.map.Group({ volatility: true });
        dragableZoneObject.setRemoteId('dragableZoneObject');
        map.addObject(dragableZoneObject);

        const div = document.createElement('div');
        const label = document.createElement('label');
        div.setAttribute('class', 'markerZone');

        label.style.fontSize = `${k(18, values.radius)}px`;
        label.style.marginTop = `${k(25, values.radius)}px`;
        label.innerText = values.zoneName;
        div.appendChild(label);

        const position = { lat: values.latitude, lng: values.longitude }

        const size = { w: k(38, values.radius), h: k(55, values.radius) };
        const svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="${size.w}" height="${size.h}" viewBox="0 0 24 24"><path fill="${values.color}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`;
        const marker = new H.map.Marker(position, {
            icon: new H.map.Icon(svgIcon, { size }),
            volatility: true
        });
        marker.draggable = true;

        const textLabel = new H.map.DomMarker(position, {
            icon: new H.map.DomIcon(div)
        });

        const radiusMeters = values.radius * 1609.34;
        const circleMain = new H.map.Circle(position, radiusMeters, {
            style: { lineWidth: 3, strokeColor: values.color, fillColor: `${values.color}55` }
        });
        dragableZoneObject.addObjects([marker, textLabel, /*circleShadow, */circleMain]);

        const handleDragStart = ev => {
            if (ev.target !== marker) return;
            const pointer = ev.currentPointer;
            const pos = map.geoToScreen(marker.getGeometry());
            marker.offset = new H.math.Point(pointer.viewportX - pos.x, pointer.viewportY - pos.y);
            map.getViewPort().element.style.cursor = 'grabbing';
            behavior.disable();
        };

        const handleDrag = ev => {
            if (ev.target !== marker || !marker.offset) return;
            const pointer = ev.currentPointer;
            const newGeo = map.screenToGeo(
                pointer.viewportX - marker.offset.x,
                pointer.viewportY - marker.offset.y
            );
            marker.setGeometry(newGeo);
            textLabel.setGeometry(newGeo);
            circleMain.setCenter(newGeo);
        };

        const handleDragEnd = ev => {
            if (ev.target !== marker) return;
            map.getViewPort().element.style.cursor = '';
            behavior.enable();
            const newGeo = marker.getGeometry();
            setValues({
                latitude: newGeo.lat,
                longitude: newGeo.lng
            });
        };

        const handleMapClick = (ev) => {
            const clickedGeo = ev.target instanceof H.map.Marker
                ? ev.target.getGeometry()
                : ev.currentPointer && map.screenToGeo(ev.currentPointer.viewportX, ev.currentPointer.viewportY);

            if (!clickedGeo) return;

            setValues({
                latitude: clickedGeo.lat,
                longitude: clickedGeo.lng
            });
        };

        map.addEventListener('dragstart', handleDragStart, false);
        map.addEventListener('drag', handleDrag, false);
        map.addEventListener('dragend', handleDragEnd, false);
        map.addEventListener('tap', handleMapClick, false);

        return () => {
            map.removeEventListener('dragstart', handleDragStart);
            map.removeEventListener('drag', handleDrag);
            map.removeEventListener('dragend', handleDragEnd);
            map.removeEventListener('tap', handleMapClick);
        };
    }, [map, behavior, values.type, values.latitude, values.longitude, values.radius, values.zoneName, values.color]);

    useEffect(() => {
        if (!map) return;
        if (values.type !== 'POLYGON') return;

        if (mode === 'EDIT') {
            detachEditedZoneGroup();
        }

        map.getObjects().forEach(obj => {
            if (
                obj.getRemoteId?.() === 'drawingPolygon' ||
                obj.getRemoteId?.() === 'drawingPolyline'
            ) {
                map.removeObject(obj);
            }
        });

        const coords = values.polygonCoords;

        if (!coords?.length) return;

        if (coords.length === 2) {
            const lineString = new H.geo.LineString();

            coords.forEach(point => {
                lineString.pushLatLngAlt(
                    Number(point.lat),
                    Number(point.lng),
                    0
                );
            });

            const polyline = new H.map.Polyline(lineString, {
                style: {
                    strokeColor: values.color || '#016BFC',
                    lineWidth: 3
                }
            });
            drawingPolylineRef.current = polyline;

            polyline.setRemoteId('drawingPolyline');

            map.addObject(polyline);

            return;
        }

        if (coords.length < 3) return;

        const strip = new H.geo.LineString();

        coords.forEach(p => {
            strip.pushLatLngAlt(
                Number(p.lat),
                Number(p.lng),
                0
            );
        });

        const geoPolygon = new H.geo.Polygon(strip);

        const polygonColor = values.color || '#016BFC';

        const centerLat = coords.reduce((sum, p) => sum + Number(p.lat), 0) / coords.length;
        const centerLng = coords.reduce((sum, p) => sum + Number(p.lng), 0) / coords.length;
        const centerPosition = { lat: centerLat, lng: centerLng };

        const drawingPolygonGroup = new H.map.Group({ volatility: true });
        drawingPolygonGroup.setRemoteId('drawingPolygon');

        const polygon = new H.map.Polygon(geoPolygon, {
            style: {
                fillColor: `${polygonColor}55`,
                strokeColor: polygonColor,
                lineWidth: 3
            }
        });
        drawingPolygonRef.current = polygon;

        const pinDiv = document.createElement('div');
        const pinLabel = document.createElement('label');
        pinDiv.setAttribute('class', 'markerZone');
        pinLabel.style.fontSize = '18px';
        pinLabel.style.marginTop = '25px';
        pinLabel.innerText = values.zoneName;
        pinDiv.appendChild(pinLabel);
        const pinTextLabel = new H.map.DomMarker(centerPosition, {
            icon: new H.map.DomIcon(pinDiv)
        });
        pinTextLabel.setData({ domElement: pinDiv });

        const pinSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="38" height="55" viewBox="0 0 24 24"><path fill="${polygonColor}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`;
        const pinMarker = new H.map.Marker(centerPosition, {
            icon: new H.map.Icon(pinSvg, { size: { w: 38, h: 55 } }),
            volatility: true
        });

        const centerDraggable = polygonMode === 'none';
        pinMarker.draggable = centerDraggable;

        drawingPolygonGroup.addObjects([polygon, pinMarker, pinTextLabel]);
        map.addObject(drawingPolygonGroup);

        if (!centerDraggable) {
            return;
        }

        let dragOffset = null;

        const handleCenterDragStart = (ev) => {
            if (ev.target !== pinMarker) return;
            const pointer = ev.currentPointer;
            const pos = map.geoToScreen(pinMarker.getGeometry());
            dragOffset = new H.math.Point(pointer.viewportX - pos.x, pointer.viewportY - pos.y);
            map.getViewPort().element.style.cursor = 'grabbing';
            behavior.disable();
        };

        const handleCenterDrag = (ev) => {
            if (ev.target !== pinMarker || !dragOffset) return;
            const pointer = ev.currentPointer;
            const newGeo = map.screenToGeo(
                pointer.viewportX - dragOffset.x,
                pointer.viewportY - dragOffset.y
            );

            const deltaLat = newGeo.lat - centerLat;
            const deltaLng = newGeo.lng - centerLng;

            const shiftedCoords = coords.map(p => ({
                lat: Number(p.lat) + deltaLat,
                lng: Number(p.lng) + deltaLng,
            }));

            const liveStrip = new H.geo.LineString();
            shiftedCoords.forEach(p => liveStrip.pushLatLngAlt(Number(p.lat), Number(p.lng), 0));
            polygon.setGeometry(new H.geo.Polygon(liveStrip));

            pinMarker.setGeometry(newGeo);
            pinTextLabel.setGeometry(newGeo);
        };

        const handleCenterDragEnd = (ev) => {
            if (ev.target !== pinMarker) return;
            map.getViewPort().element.style.cursor = '';
            behavior.enable();

            const newGeo = pinMarker.getGeometry();
            const deltaLat = newGeo.lat - centerLat;
            const deltaLng = newGeo.lng - centerLng;

            dragOffset = null;

            setValues({
                polygonCoords: coords.map(p => ({
                    lat: Number(p.lat) + deltaLat,
                    lng: Number(p.lng) + deltaLng,
                })),
            });
        };

        map.addEventListener('dragstart', handleCenterDragStart);
        map.addEventListener('drag', handleCenterDrag);
        map.addEventListener('dragend', handleCenterDragEnd);

        return () => {
            map.removeEventListener('dragstart', handleCenterDragStart);
            map.removeEventListener('drag', handleCenterDrag);
            map.removeEventListener('dragend', handleCenterDragEnd);
        };
    }, [
        map,
        behavior,
        values.type,
        values.polygonCoords,
        values.color,
        values.zoneName,
        polygonMode
    ]);

    useEffect(() => {
        if (!map) return;
        if (values.type !== 'POLYGON') return;

        map.getObjects().forEach(obj => {
            if (obj.getRemoteId?.() === 'polygonVertex') {
                map.removeObject(obj);
            }
        });

        if (polygonMode === 'none') {
            return;
        }

        values.polygonCoords.forEach((point, index) => {
            const marker = new H.map.Marker(
                point,
                {
                    icon: createVertexIcon(
                        values.color,
                        polygonMode
                    ),
                    volatility: true
                }
            );
            marker.setRemoteId('polygonVertex');
            marker.draggable = polygonMode === 'edit';
            marker.setData({
                pointIndex: index
            });
            map.addObject(marker);
        });

    }, [map, values.type, values.polygonCoords, values.color, polygonMode]);

    const tapTimeoutRef = useRef(null);

    useEffect(() => {
        if (!map) return;
        if (values.type !== 'POLYGON') return;
        if (polygonMode !== 'create') return;

        const handleTap = (ev) => {
            clearTimeout(tapTimeoutRef.current);

            tapTimeoutRef.current = setTimeout(() => {
                addPolygonPointFromEvent(ev);
            }, 150);
        };

        const handleDoubleTap = (ev) => {
            clearTimeout(tapTimeoutRef.current);

            const added = addPolygonPointFromEvent(ev);

            if (!added || polygonCoordsRef.current.length + 1 < 3) {
                return;
            }

            finishPolygon();
        };


        map.addEventListener('tap', handleTap);
        map.addEventListener('dbltap', handleDoubleTap);

        return () => {
            clearTimeout(tapTimeoutRef.current);

            map.removeEventListener('tap', handleTap);
            map.removeEventListener('dbltap', handleDoubleTap);
        };
    }, [map, values.type, polygonMode]);

    useEffect(() => {
        if (!map) return;
        if (values.type !== 'POLYGON') return;
        if (polygonMode !== 'edit') return;

        const handleVertexDragStart = (ev) => {
            const marker = ev.target;

            if (!(marker instanceof H.map.Marker) || marker.getRemoteId?.() !== 'polygonVertex') {
                return;
            }

            behavior.disable();
        };

        const handleVertexDrag = (ev) => {
            const marker = ev.target;

            if (!(marker instanceof H.map.Marker) || marker.getRemoteId?.() !== 'polygonVertex') {
                return;
            }

            const geo = map.screenToGeo(
                ev.currentPointer.viewportX,
                ev.currentPointer.viewportY
            );

            marker.setGeometry(geo);

            const previewCoords = [...polygonCoordsRef.current];
            previewCoords[marker.getData().pointIndex] = {
                lat: geo.lat,
                lng: geo.lng
            };
            if (drawingPolygonRef.current && previewCoords.length >= 3) {
                const strip = new H.geo.LineString();

                previewCoords.forEach(point => {
                    strip.pushLatLngAlt(
                        Number(point.lat),
                        Number(point.lng),
                        0
                    );
                });

                drawingPolygonRef.current.setGeometry(
                    new H.geo.Polygon(strip)
                );
            } else if (drawingPolylineRef.current && previewCoords.length === 2) {
                const lineString = new H.geo.LineString();

                previewCoords.forEach(point => {
                    lineString.pushLatLngAlt(
                        Number(point.lat),
                        Number(point.lng),
                        0
                    );
                });

                drawingPolylineRef.current.setGeometry(
                    lineString
                );
            }
        };

        const handleVertexDragEnd = (ev) => {
            const marker = ev.target;

            if (!(marker instanceof H.map.Marker) || marker.getRemoteId?.() !== 'polygonVertex') {
                return;
            }

            const geo = map.screenToGeo(
                ev.currentPointer.viewportX,
                ev.currentPointer.viewportY
            );
            dispatch({
                type: 'MOVE_POLYGON_POINT',
                payload: {
                    index: marker.getData().pointIndex,
                    lat: geo.lat,
                    lng: geo.lng
                }
            });

            behavior.enable();
        };

        map.addEventListener('dragstart', handleVertexDragStart);
        map.addEventListener('drag', handleVertexDrag);
        map.addEventListener('dragend', handleVertexDragEnd);

        return () => {
            map.removeEventListener('dragstart', handleVertexDragStart);
            map.removeEventListener('drag', handleVertexDrag);
            map.removeEventListener('dragend', handleVertexDragEnd);
        };
    }, [map, behavior, polygonMode, values.type]);

    useEffect(() => {
        if (values.type !== 'CIRCLE') return;

        setAddressByCoords(values.latitude, values.longitude);
    }, [values.type, values.latitude, values.longitude]);

    useEffect(() => {
        if (values.type !== 'POLYGON') return;

        if (values.polygonCoords.length < 3) return;

        const centerLat = values.polygonCoords.reduce((sum, p) => sum + Number(p.lat), 0) / values.polygonCoords.length;
        const centerLng = values.polygonCoords.reduce((sum, p) => sum + Number(p.lng), 0) / values.polygonCoords.length;

        setAddressByCoords(centerLat, centerLng);
    }, [values.type, values.polygonCoords]);

    const setMode = useCallback((payload) => {
        dispatch({ type: 'SET_MODE', payload })
    }, [dispatch]);

    const setValues = useCallback((payload) => {
        dispatch({ type: 'SET_ZONE_DATA_VALUES', payload });
    }, [dispatch]);

    const setPresetColors = useCallback((payload) => {
        dispatch({ type: 'SET_PRESET_COLORS', payload });
    }, [dispatch]);

    const addPolygonPointFromEvent = (ev) => {
        const geo = map.screenToGeo(
            ev.currentPointer.viewportX,
            ev.currentPointer.viewportY
        );

        if (!geo) {
            return false;
        }

        dispatch({
            type: 'ADD_POLYGON_POINT',
            payload: {
                lat: geo.lat,
                lng: geo.lng
            }
        });

        return true;
    };

    const finishPolygon = () => {
        setPolygonMode('none');

        behavior.enable(
            H.mapevents.Behavior.Feature.DBL_TAP_ZOOM
        );
    };

    const setAddressByCoords = (lat, lng) => {
        const searchService = platform.getSearchService();
        searchService.reverseGeocode(
            {
                at: `${lat},${lng}`,
                limit: 1
            },
            result => {
                const location = result.items?.[0];
                setValues({
                    address: location ? location.address.label : '',
                });
            },
            error => {
                console.error("Geocoding error:", error);
            }
        );
    }

    const resetMap = () => {
        if (!map) return;

        const objectsToRemove = map.getObjects().filter(obj => {
            const id = obj.getRemoteId?.();

            return (
                id === 'dragableZoneObject' ||
                id === 'drawingPolygon' ||
                id === 'drawingPolyline' ||
                id === 'polygonVertex'
            );
        });

        map.removeObjects(objectsToRemove);
    };

    const changeStyle = (shape, color) => {
        shape.setVisibility(true);

        const token = Symbol('zone-animation');
        animationTokens.current.set(shape, token);

        shape.setStyle({
            fillColor: `${color}55`,
            lineWidth: 20,
            strokeColor: `${color}85`
        });

        setTimeout(() => {
            setTimeout(() => {
                if (animationTokens.current.get(shape) === token) {
                    fadeOutCircle(shape, 2000, color, token);
                }
            }, 3000);
        }, 50);
    };

    const fadeOutCircle = (shape, duration = 2000, color, token) => {
        const start = performance.now();
        const fillColor = `${color}55`;
        const startAlpha = 0x85;

        const animate = (time) => {
            if (animationTokens.current.get(shape) !== token) {
                return;
            }

            const elapsed = time - start;
            const progress = Math.min(elapsed / duration, 1);
            const newWidth = 20 - (20 * progress);
            const newAlpha = Math.round(startAlpha * (1 - progress));
            const alphaHex = newAlpha.toString(16).padStart(2, '0');

            shape.setStyle({
                fillColor,
                lineWidth: newWidth,
                strokeColor: `${color}${alphaHex}`
            });

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                shape.setStyle({ fillColor, lineWidth: 0, strokeColor: `${color}00` });
                animationTokens.current.delete(shape);
            }
        };

        requestAnimationFrame(animate);
    };

    const findAllZonesGroup = () => map?.getObjects().find(
        obj => obj instanceof H.map.Group && obj.getRemoteId() === 'allZonesObject'
    );

    const detachEditedZoneGroup = () => {
        const allZonesGroup = findAllZonesGroup();
        if (!allZonesGroup) return;

        const zoneGroup = allZonesGroup.getObjects().find(
            obj => obj instanceof H.map.Group && obj.getRemoteId() === editedZoneId
        );
        if (!zoneGroup) return;

        editedZoneGroupRef.current = cloneZoneGroup(zoneGroup);
        allZonesGroup.removeObject(zoneGroup);
    };

    const restoreEditedZoneGroup = () => {
        const allZonesGroup = findAllZonesGroup();
        const editedZoneGroup = editedZoneGroupRef.current;
        if (!allZonesGroup || !editedZoneGroup) return;

        allZonesGroup.addObject(editedZoneGroup);

        const shape = editedZoneGroup.getObjects().find(obj =>
            obj instanceof H.map.Polygon || obj instanceof H.map.Polyline
        );
        if (shape) {
            const zone = zones.find(z => z.id === editedZoneId);
            changeStyle(shape, zone?.shape_color || values.color);
        }

        editedZoneGroupRef.current = null;
    };

    const cloneZoneGroup = (zoneGroup) => {
        const sourceZone = zones.find(z => z.id === zoneGroup.getRemoteId());
        const zoneColor = sourceZone?.shape_color || values.color;
        const markerRadius = sourceZone?.circle_radius ?? values.radius;
        const isCircleZone = (sourceZone?.shape_type || values.type) === 'CIRCLE';

        const group = new H.map.Group();
        group.setRemoteId(zoneGroup.getRemoteId());

        const clonedObjects = [];
        let clonedShape = null;

        zoneGroup.getObjects().forEach(obj => {
            let copy;

            if (obj instanceof H.map.Circle) {
                copy = new H.map.Circle(obj.getCenter(), obj.getRadius(), {
                    style: { ...obj.getStyle() }
                });
                clonedShape = copy;
            }

            if (obj instanceof H.map.Polygon && !(obj instanceof H.map.Circle)) {
                const originalExterior = obj.getGeometry().getExterior();
                const clonedExterior = new H.geo.LineString();

                for (let i = 0; i < originalExterior.getPointCount(); i++) {
                    clonedExterior.pushPoint(originalExterior.extractPoint(i));
                }

                copy = new H.map.Polygon(new H.geo.Polygon(clonedExterior), {
                    style: { ...obj.getStyle() }
                });
                clonedShape = copy;
            }

            if (obj instanceof H.map.Polyline) {
                const originalGeometry = obj.getGeometry();
                const clonedGeometry = new H.geo.LineString();

                for (let i = 0; i < originalGeometry.getPointCount(); i++) {
                    clonedGeometry.pushPoint(originalGeometry.extractPoint(i));
                }

                copy = new H.map.Polyline(clonedGeometry, {
                    style: { ...obj.getStyle() }
                });
                clonedShape = copy;
            }

            if (obj instanceof H.map.DomMarker) {
                const originalElement = obj.getData()?.domElement;
                if (originalElement) {
                    const clonedElement = originalElement.cloneNode(true);
                    copy = new H.map.DomMarker(obj.getGeometry(), {
                        icon: new H.map.DomIcon(clonedElement)
                    });
                    copy.setData({ domElement: clonedElement });
                }
            }

            if (obj instanceof H.map.Marker) {
                const size = isCircleZone
                    ? { w: k(38, markerRadius), h: k(55, markerRadius) }
                    : { w: 38, h: 55 };
                const svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="${size.w}" height="${size.h}" viewBox="0 0 24 24"><path fill="${zoneColor}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`;
                copy = new H.map.Marker(obj.getGeometry(), {
                    icon: new H.map.Icon(svgIcon, { size }),
                });
                copy.draggable = obj.draggable;
            }

            if (copy) clonedObjects.push(copy);
        });

        group.addObjects(clonedObjects);
        if (clonedShape) {
            group.addEventListener('tap', () => changeStyle(clonedShape, zoneColor));
        }

        return group;
    };

    const validate = async () => {
        const newErrors = {...defaultErrors};

        if (!values.zoneName.trim()) {
            newErrors.zoneName = t('errNoName');
        } else if (values.zoneName.length > 50) {
            newErrors.zoneName = t('errNameLength');
        }

        if (!values.address.trim()) {
            newErrors.address = t('errNoAddress');
        }

        if (values.type === 'CIRCLE') {
            const lat = Number(values.latitude);
            if (Number.isNaN(lat) || lat < -90 || lat > 90) {
                newErrors.latitude = t('errLatitude');
            }

            const lng = Number(values.longitude);
            if (Number.isNaN(lng) || lng < -180 || lng > 180) {
                newErrors.longitude = t('errLongitude');
            }

            const rad = Number(values.radius);
            if (Number.isNaN(rad) || rad < 0.1 || rad > 5.0) {
                newErrors.radius = t('errRadius');
            }
        }

        if (values.type === 'POLYGON') {
            const hasInvalidPoint = values.polygonCoords.some(point => {
                const lat = Number(point.lat);
                const lng = Number(point.lng);

                return (
                    point.lat === '' ||
                    point.lng === '' ||
                    Number.isNaN(lat) ||
                    Number.isNaN(lng) ||
                    lat < -90 ||
                    lat > 90 ||
                    lng < -180 ||
                    lng > 180
                );
            });

            if (values.polygonCoords.length < 3) {
                newErrors.coordinates = t('polygonMin3Points');
            } else if (hasInvalidPoint) {
                newErrors.coordinates = t('polygonInvalidCoordinates');
            } else {
                const uniquePoints = new Set(values.polygonCoords.map(p => `${p.lat}:${p.lng}`));

                if (uniquePoints.size < 3) {
                    newErrors.coordinates = t('polygonUniquePoints');
                } else if (!isSimplePolygon(values.polygonCoords)) {
                    newErrors.coordinates = t('polygonSelfIntersecting');
                } else {
                    const feature = getPolygonFeature(values.polygonCoords);
                    const polygonArea = area(feature);

                    if (polygonArea <= 0) {
                        newErrors.coordinates = t('polygonAreaInvalid');
                    }
                }
            }
        }

        setErrors(newErrors);
        return Object.values(newErrors).every((msg) => msg === '');
    };

    const checkZoneName = (name) => {
        if (zoneNames.includes(name.toLowerCase())) {
            setErrors(prev => ({
                ...prev,
                zoneName: t('errorZoneExists')
            }));
        }
    };

    const onChangeField = (key) => (e) => {
        const value = key === 'radiusSlider' ? e[1] : e.target?.value;
        const stateKey = key === 'radiusSlider' ? 'radius' : key;

        if (stateKey === 'type') {
            resetMap();
            setPolygonMode('none');
            dispatch({ type: 'CLEAR_POLYGON' });
        }

        setValues({
            [stateKey]: key === 'radius' ? +value : value
        });

        setErrors(prev => ({
            ...prev,
            [stateKey]: ''
        }));

        if (stateKey === 'zoneName') {
            checkZoneName(value);
        }

        if (stateKey !== 'address') return;

        if (!value || value.length < 3) {
            setSuggestions([]);
            return;
        }

        const searchService = platform.getSearchService();
        const center = map.getCenter();
        const validTypes = [
            "houseNumber",
            "street",
            "place",
            "locality",
            "park",
            "square",
            "administrativeArea",
            "intersection"
        ];

        searchService.autosuggest(
            {
                q: value,
                at: `${center.lat},${center.lng}`
            },
            result => {
                const filtered = result.items.filter(item =>
                    validTypes.includes(item.resultType) && item.address?.label
                );
                setSuggestions(filtered);
            },
            error => {
                console.error('Autosuggest error:', error);
                setSuggestions([]);
            }
        );
    };

    const changeColor = (color, updatePreset) => {
        if (color !== values.color) {
            setValues({
                color,
            });
            if (updatePreset) {
                setPresetColors(color);
            }
        }
    };

    const changePolygonPoint = (index, key) => (e) => {
        const value = e.target.value;
        dispatch({
            type: 'EDIT_POLYGON_POINT',
            payload: {
                index,
                key,
                value,
            }
        })
    }

    const clearPolygonPoint = (index) => {
        dispatch({
            type: 'CLEAR_POLYGON_POINT',
            payload: {
                index,
            }
        })
    }

    const handleSuggestionSelect = (item) => {
        const label = item.address.label;
        const { lat, lng } = item.position;

        if (values.type === 'POLYGON') {
            if (values.polygonCoords.length) {
                const centerLat = values.polygonCoords.reduce((sum, p) => sum + Number(p.lat), 0) / values.polygonCoords.length;
                const centerLng = values.polygonCoords.reduce((sum, p) => sum + Number(p.lng), 0) / values.polygonCoords.length;
                const deltaLat = lat - centerLat;
                const deltaLng = lng - centerLng;

                setValues({
                    address: label,
                    polygonCoords: values.polygonCoords.map(point => ({
                        ...point,
                        lat: Number(point.lat) + deltaLat,
                        lng: Number(point.lng) + deltaLng,
                    })),
                });
            } else {
                setValues({ address: label });
            }
        } else {
            setValues({
                address: label,
                latitude: lat,
                longitude: lng,
            });
        }

        setSuggestions([]);

        if (item.mapView) {
            const { north, south, east, west } = item.mapView;
            const bounds = new H.geo.Rect(south, west, north, east);
            map.getViewModel().setLookAtData({ bounds });
        } else {
            map.setCenter({ lat, lng });
            map.setZoom(16);
        }
    };

    const cancelForm = () => {
        resetMap();
        setPolygonMode('none');
        dispatch({
            type: 'RESET_ZONE_DATA',
            payload: {
                type: '',
                latitude: mapState.lat,
                longitude: mapState.lng
            }
        });

        restoreEditedZoneGroup();
        setErrors({...defaultErrors});
        setMode(hasZones ? 'LIST' : 'NO_ZONES');
    }

    const getZonePayload = () => {
        const common = {
            shape_type: values.type,
            zone_name: values.zoneName,
            address: values.address,
            shape_color: values.color,
            user_name: `${user.first_name} ${user.last_name}`,
            user_email: user.email,
        };

        if (values.type === 'CIRCLE') {
            return {
                ...common,
                shape_points:[{
                    lat: values.latitude,
                    lng: values.longitude,
                }],
                circle_radius: values.radius,
            };
        }

        return {
            ...common,
            shape_points: values.polygonCoords,
        };
    };

    const submitZone = async () => {
        const isValid = await validate();
        if (!isValid) return;

        const payload = getZonePayload();
        if (mode === 'CREATE') {
            createZone(companyId, payload).then(res => {
                const result = res.data.result;
                if (result === 'OK') {
                    resetMap();
                    getZones(true);
                    toastr.success("", t('successCreatingZone'));
                } else {
                    toastr.error(t('errorCreatingZone'));
                }
            }).catch(err => {
                console.log(err, "create Zone error");
                toastr.error(t('errorCreatingZone'));
            });
        } else if (mode === 'EDIT') {
            editZone(companyId, editedZoneId, payload).then(res => {
                const result = res.data.result;
                if (result === 'OK') {
                    resetMap();
                    getZones(true);
                    toastr.success("", t('successEditingZone'));
                } else {
                    toastr.error(t('errorEditingZone'));
                }
            }).catch(err => {
                console.log(err, "edit Zone error");
                toastr.error(t('errorEditingZone'));
            });
        }
    };

    const startCreate = () => {
        setPolygonMode('create');
    };

    const undoPoint = () => {
        if (polygonMode === 'edit') {
            dispatch({ type: 'UNDO_POLYGON_HISTORY' });
            return;
        }

        dispatch({ type: 'UNDO_POLYGON_POINT' });
    };

    const deletePolygon = () => {
        dispatch({ type: 'CLEAR_POLYGON' });

        map.getObjects().forEach(obj => {
            const id = obj.getRemoteId?.();
            if (['drawingPolygon', 'drawingPolyline', 'polygonVertex'].includes(id)) {
                map.removeObject(obj);
            }
        });
    };

    const popover = (
        <div
            style={{
                position: 'absolute',
                backgroundColor: 'rgba(255, 100, 100, 0.85)',
                color: 'white',
                borderRadius: 8,
                zIndex: 1,
                overflow: 'hidden',
            }}
        >
            <HexColorPicker color={values.color} onChangeEnd={(color) => {
                changeColor(color, true);
            }} />
        </div>
    );

    return (
        <div className="new-zone-form">
            {
                values.type === 'POLYGON' && (
                    <div className="drawing-tools">
                        <Button
                            className={polygonMode === 'create' ? 'selected' : ''}
                            variant="outline-primary"
                            onClick={startCreate}
                        >
                            <TbMapPinPlus size={22} />
                            <span>{t('addPoint')}</span>
                        </Button>
                        <Button
                            className={polygonMode === 'edit' ? 'selected' : ''}
                            variant="outline-primary"
                            onClick={() => setPolygonMode('edit')}
                        >
                            <PiPolygonDuotone size={22} />
                            <span>{t('editPoints')}</span>
                        </Button>
                        <Button
                            variant="outline-primary"
                            onClick={undoPoint}
                        >
                            <GrUndo size={22} />
                            <span>{t('undo')}</span>
                        </Button>
                        <Button
                            variant="outline-primary"
                            onClick={deletePolygon}
                        >
                            <RiDeleteBinLine size={22} />
                            <span>{t('clearShape')}</span>
                        </Button>
                    </div>
                )
            }
            <Form>
                <h4>1. {t('chooseZoneType')}</h4>
                <ButtonGroup className="zone-type-selector">
                    {
                        selectorTypeData.map(item => (
                            <label key={`type-selector-${item.type.toLowerCase()}`}
                                   className={values.type === item.type ? 'selected' : ''}>
                                <Form.Check
                                    type="radio"
                                    value={item.type}
                                    checked={values.type === item.type}
                                    onChange={onChangeField('type')}
                                />
                                <div className="zone-type-info">
                                    <span className="label-type">{item.label}</span>
                                    <span className="label-type-caption">{item.labelCaption}</span>
                                </div>
                            </label>
                        ))
                    }
                </ButtonGroup>
                {values.type === 'POLYGON' ? (
                    <Alert variant="light" className="selector-info">
                        <div className="info-block">
                            <div><PiInfo color="#0071b6" size="1.5em"/></div>
                            <span>{t('customShapeLabel1')}</span>
                        </div>
                        <div className="info-block">
                            <div><PiInfo color="#0071b6" size="1.5em"/></div>
                            <span>{t('customShapeLabel2')}</span>
                        </div>
                    </Alert>
                ) : ''}
                <h4>2. {t('zoneDetails')}</h4>
                <Form.Group>
                    <Form.Label>{t('zoneName')}</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="e.g., North Lot"
                        value={values.zoneName}
                        isInvalid={!!errors.zoneName}
                        onChange={onChangeField('zoneName')}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errors.zoneName}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="address-form-group">
                    <Form.Label>{t('address')}</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder={`${t('address')}...`}
                        value={values.address}
                        isInvalid={!!errors.address}
                        onChange={onChangeField('address')}
                        onBlur={() => setTimeout(() => setSuggestions([]), 100)}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errors.address}
                    </Form.Control.Feedback>
                    {suggestions.length > 0 && (
                        <ul className="suggestion-list">
                            {suggestions.map((item, idx) => (
                                <li
                                    key={idx}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleSuggestionSelect(item)}
                                >
                                    {item.address.label}
                                </li>
                            ))}
                        </ul>
                    )}
                </Form.Group>
                <Form.Group>
                    <Form.Label>{t('zoneColor')}</Form.Label>
                    <ButtonGroup className="flex-wrap align-items-center">
                        {presetColors.map((color, idx) => (
                            <div
                                key={`preset-color-${idx}`}
                                style={{
                                    borderRadius: "6px",
                                    border: `1px solid ${values.color === color ? color : 'transparent'}`
                                }}
                            >
                                <div
                                    onClick={() => changeColor(color)}
                                    className="color-swatch"
                                    style={{
                                        backgroundColor: color,
                                        width: "25px",
                                        height: "25px",
                                        borderRadius: "6px",
                                        margin: "2px",
                                        cursor: "pointer",
                                        position: "relative",
                                    }}
                                >
                                    {values.color === color && (
                                        <FaCheck
                                            color={getContrastColor(color)}
                                            style={{
                                                position: "absolute",
                                                top: "50%",
                                                left: "50%",
                                                transform: "translate(-50%, -50%)"
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        ))}
                        <OverlayTrigger
                            trigger="click"
                            placement="right-end"
                            popperConfig={{
                                modifiers: [
                                    {
                                        name: 'offset',
                                        options: {
                                            offset: [0, 3],
                                        },
                                    },
                                ],
                            }}
                            overlay={popover}
                        >
                            <div
                                style={{
                                    backgroundColor: 'transparent',
                                    width: "25px",
                                    height: "25px",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    position: "relative",
                                    border: '2px solid rgb(229, 231, 235)',
                                }}
                            >
                                <FiPlus style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                }}/>
                            </div>
                        </OverlayTrigger>
                    </ButtonGroup>
                </Form.Group>
                {
                    values.type === 'CIRCLE' ? (
                        <>
                            <Form.Group>
                                <Form.Label>{t('latitude')}</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={values.latitude}
                                    isInvalid={!!errors.latitude}
                                    onChange={onChangeField('latitude')}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.latitude}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>{t('longitude')}</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={values.longitude}
                                    isInvalid={!!errors.longitude}
                                    onChange={onChangeField('longitude')}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.longitude}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="radius-form-group">
                                <Form.Label>{`${t('radius')} (${t('miles')})`}</Form.Label>
                                <Row>
                                    <Col xs={8} className="radius-slider-column">
                                        <RangeSlider
                                            min={0.01}
                                            max={5.0}
                                            step={0.01}
                                            className="radius-slider"
                                            value={[0, values.radius]}
                                            thumbsDisabled={[true, false]}
                                            rangeSlideDisabled={true}
                                            onInput={onChangeField('radiusSlider')}
                                        />
                                    </Col>
                                    <Col xs={4}>
                                        <Form.Control
                                            min={0.1}
                                            max={5.0}
                                            type="number"
                                            value={values.radius}
                                            isInvalid={!!errors.radius}
                                            onChange={onChangeField('radius')}
                                        />
                                    </Col>
                                </Row>
                                <Form.Control.Feedback className={errors.radius ? 'error-radius' : ''} type="invalid">
                                    {errors.radius}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </>
                    ) : ''
                }
                {
                    values.type === 'POLYGON' ? (
                        <>
                            <Form.Group className="coordinates-form-group">
                                <Form.Label>{t('coordinates')} {`(${values.polygonCoords.length || 0} ${values.polygonCoords.length === 1 ? t('point') : t('points')})`}</Form.Label>
                                {values.polygonCoords.length ? (
                                    <Table size="sm">
                                        <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>{t('latitude')}</th>
                                            <th>{t('longitude')}</th>
                                            <th>{t('actions')}</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {values.polygonCoords.map((coord, index) => (
                                            <tr key={index}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <Form.Control
                                                        type="number"
                                                        size="sm"
                                                        value={coord.lat}
                                                        isInvalid={!coord.lat}
                                                        onChange={changePolygonPoint(index, 'lat')}
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="number"
                                                        size="sm"
                                                        value={coord.lng}
                                                        isInvalid={!coord.lng}
                                                        onChange={changePolygonPoint(index, 'lng')}
                                                    />
                                                </td>
                                                <td>
                                                    <Button onClick={() => clearPolygonPoint(index)}>
                                                        <RiDeleteBinLine color="#212529" size={20} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </Table>
                                ) : ''}
                                <Form.Control.Feedback className={errors.coordinates ? 'error-coordinates' : ''} type="invalid">
                                    {errors.coordinates}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </>
                    ) : ''
                }
            </Form>
            <div className="zones-form-footer">
                <CustomButton variant="primary-outline" onClick={cancelForm}>{t('btnCancel')}</CustomButton>
                <CustomButton variant="primary" onClick={submitZone}>{t('btnSave')}</CustomButton>
            </div>
        </div>
    )
});

export default ZoneForm;
